import type { OwnerMaintenancePreview, OwnerMaintenanceReceipt } from './contracts'

type MaintenanceRecoveryBase = {
  version: 1
  ownerId: string
  createdAt: string
}

export type OwnerMaintenanceRecovery =
  | MaintenanceRecoveryBase & {
    phase: 'apply-pending'
    preview: OwnerMaintenancePreview
    clientMutationId: string
    confirmation: {
      typedName?: string
      backupChecksum?: string
      backupDownloadInitiated?: boolean
    }
  }
  | MaintenanceRecoveryBase & {
    phase: 'reload-after-apply'
    receipt: OwnerMaintenanceReceipt
  }

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const sha256Pattern = /^[0-9a-f]{64}$/
const actions = ['rename', 'move', 'archive', 'restore', 'delete']
const targetKinds = ['operation', 'category']

export function ownerMaintenanceRecoveryKey(ownerId: string): string {
  if (!ownerId || ownerId !== ownerId.trim()) throw new Error('Owner maintenance recovery requires an owner identity.')
  return `${ownerId}:owner-maintenance`
}

export function encodeOwnerMaintenanceRecovery(recovery: OwnerMaintenanceRecovery): string {
  return JSON.stringify(recovery)
}

function validPreview(value: unknown): value is OwnerMaintenancePreview {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const preview = value as Record<string, unknown>
  const input = preview.input as Record<string, unknown> | undefined
  return Number.isSafeInteger(preview.expectedStructureVersion) && Number.isSafeInteger(preview.expectedScoreRevision) &&
    Boolean(input && actions.includes(String(input.action)) && targetKinds.includes(String(input.targetKind)) && typeof input.targetSourceKey === 'string') &&
    typeof preview.previewFingerprint === 'string' && sha256Pattern.test(preview.previewFingerprint) &&
    typeof preview.confirmationText === 'string' && Array.isArray(preview.affectedItems) && Array.isArray(preview.categoryImpacts)
}

function validReceipt(value: unknown): value is OwnerMaintenanceReceipt {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const receipt = value as Record<string, unknown>
  return typeof receipt.mutationId === 'string' && uuidPattern.test(receipt.mutationId) &&
    actions.includes(String(receipt.action)) && targetKinds.includes(String(receipt.targetKind)) &&
    Number.isSafeInteger(receipt.structureVersion) && Number.isSafeInteger(receipt.scoreRevision) &&
    Number.isSafeInteger(receipt.affectedItems) && Number.isSafeInteger(receipt.affectedEntries) &&
    typeof receipt.idempotent === 'boolean' &&
    (receipt.backupChecksum === null || typeof receipt.backupChecksum === 'string' && sha256Pattern.test(receipt.backupChecksum))
}

export function decodeOwnerMaintenanceRecovery(raw: string | null, ownerId: string): OwnerMaintenanceRecovery | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    const recovery = value as Record<string, unknown>
    if (recovery.version !== 1 || recovery.ownerId !== ownerId || typeof recovery.createdAt !== 'string' || !Number.isFinite(Date.parse(recovery.createdAt))) return null
    if (recovery.phase === 'reload-after-apply') return validReceipt(recovery.receipt) ? value as OwnerMaintenanceRecovery : null
    if (recovery.phase !== 'apply-pending' || !validPreview(recovery.preview) || typeof recovery.clientMutationId !== 'string' || !uuidPattern.test(recovery.clientMutationId) || !recovery.confirmation || typeof recovery.confirmation !== 'object' || Array.isArray(recovery.confirmation)) return null
    const confirmation = recovery.confirmation as Record<string, unknown>
    if (!(confirmation.typedName === undefined || typeof confirmation.typedName === 'string') ||
        !(confirmation.backupChecksum === undefined || typeof confirmation.backupChecksum === 'string' && sha256Pattern.test(confirmation.backupChecksum)) ||
        !(confirmation.backupDownloadInitiated === undefined || typeof confirmation.backupDownloadInitiated === 'boolean')) return null
    const preview = recovery.preview as OwnerMaintenancePreview
    if (preview.input.action === 'delete' && (confirmation.typedName !== preview.targetName || confirmation.backupChecksum !== preview.backup?.sha256 || confirmation.backupDownloadInitiated !== true)) return null
    return value as OwnerMaintenanceRecovery
  } catch {
    return null
  }
}
